// FILE: components/shared/UserLink.tsx
"use client";

interface UserLinkProps {
  /** Display name */
  name: string;
  /** User ID for routing (e.g. "nguyen-minh-tuan") */
  userId?: string;
  /** Callback to navigate within AppShell SPA routing */
  onNavigate?: (page: string) => void;
  /** Extra Tailwind classes */
  className?: string;
}

/**
 * UserLink — Clickable user name used globally across the app.
 *
 * Usage:
 *   <UserLink name="Nguyễn Minh Tuấn" userId="nguyen-minh-tuan" onNavigate={onNavigate} />
 *
 * Default style: teal-700, hover orange with underline.
 * When userId & onNavigate are provided, clicking navigates to the profile page.
 */
export default function UserLink({
  name,
  userId,
  onNavigate,
  className = "",
}: UserLinkProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onNavigate) {
      onNavigate("profile");
    }
  };

  const isClickable = !!onNavigate;

  return (
    <span
      onClick={isClickable ? handleClick : undefined}
      className={`
        font-semibold text-teal-700 transition-colors
        ${isClickable ? "hover:text-orange-500 hover:underline cursor-pointer" : ""}
        ${className}
      `.trim()}
      role={isClickable ? "link" : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onKeyDown={isClickable ? (e) => { if (e.key === "Enter") handleClick(e as any); } : undefined}
    >
      {name}
    </span>
  );
}
